import * as R from 'ramda'
import moment from 'moment'
import { ALL_PRODUCTS_SCOPE_URI, ALL_PRODUCTS_TYPE } from 'constants/navigation'
import * as RE from 'helpers/ramda-ext/'
import { curryExts } from 'helpers/ramda-ext/curry'
import { State } from 'store/reducers/index.types'
import {
  FiltersProducts,
  Resource,
  Resources,
  ResourceWithParent
} from 'types/typescript'
import {
  FindPrimaryNavigationNameInAncestors,
  FindSubNavigationNameInAncestors,
  FormatDate,
  GetCityByCode,
  GetFirstCatalogAncestor,
  GetFormattedDepartureDate,
  GetProductTitle,
  GetResourcePathname,
  GetVPBonus,
  IsInAllProductsScope,
  IsNotPrimaryNavigation,
  IsNotTypeProduct,
  IsParent,
  IsPrimaryNavigation,
  IsProductFetched,
  IsSaleKey,
  IsTypeCatalog,
  IsTypeNavigation,
  IsTypePackage,
  IsTypeProduct,
  IsTypeSale,
  IsValidResource,
  RetrievedAncestors,
  WhenPackage,
  WhenProduct
} from './resources.types'

export const isParent: IsParent = parent => child => R.contains(child.key, parent.resources)
export const isProductFetched: IsProductFetched = productKey => R.path(['resources', productKey, 'fetched'])
export const isType = curryExts((type: string, resource: Resource): boolean => R.propEq('type', type, resource))

export const isTypeSale: IsTypeSale = isType('sale')
export const isTypeNavigation: IsTypeNavigation = isType('navigation')
export const isTypeCatalog: IsTypeCatalog = R.either(isTypeSale, isTypeNavigation)
export const isTypeProduct: IsTypeProduct = isType('product')
export const isNotTypeProduct: IsNotTypeProduct = R.complement(isType('product'))
export const isTypePackage: IsTypePackage = isType('package')

export const isSaleKey: IsSaleKey = key => R.propEq('sale', key)

export const getResourceByKey = curryExts((state: State, key: string) => R.pipe(
  R.path(['resources', key]),
  R.defaultTo({})
)(state))

export const whenPackage: WhenPackage = R.when(isTypePackage)
export const whenProduct: WhenProduct = R.when(isTypeProduct)

export const isValidResource: IsValidResource = R.allPass([
  RE.isNotNil,
  RE.hasOf(String, 'type'),
  RE.hasOf(String, 'key')
])

export const isPrimaryNavigation: IsPrimaryNavigation = res => (res.parent === res.sale)
export const isNotPrimaryNavigation: IsNotPrimaryNavigation = res => (res.parent !== res.sale)

export const findParent = curryExts((
  child: Resource,
  allResources: Resources
) => (
  child as ResourceWithParent).parent
  ? allResources[(child as ResourceWithParent).parent]
  : null
)

export const isInAllProductsScope: IsInAllProductsScope = (
  scope,
  resource
) => (
  scope && isTypeProduct(resource) && isType(ALL_PRODUCTS_TYPE)(scope)
)

const retrievedAncestors: RetrievedAncestors = (
  alreadyFoundAncestors,
  allResources,
  resource
) => {
  const immediateParent = allResources[(resource as ResourceWithParent).parent]

  if (isValidResource(immediateParent)) {
    const withParent = R.append(immediateParent, alreadyFoundAncestors)
    return retrievedAncestors(withParent, allResources, immediateParent)
  }

  return alreadyFoundAncestors
}

export const getAllAncestors = curryExts(retrievedAncestors)

export const getResourcePathname: GetResourcePathname = (
  opeCode: string,
  allResources: Resources,
  scope: Resource,
  resource: Resource
): string => (
  R.pipe(
    R.reverse,
    R.append(resource),
    R.when(
      R.always(isInAllProductsScope(scope, resource)),
      R.pipe(
        R.filter(R.complement(isTypeNavigation)),
        R.insert(1, { slug: ALL_PRODUCTS_SCOPE_URI, type: ALL_PRODUCTS_TYPE })
      )
    ),
    R.pluck('slug'),
    R.when(
      R.always(isTypeProduct(resource)),
      slugs => R.insert(R.length(slugs) - 1, 'detail', slugs)
    ),
    R.intersperse('/'),
    R.prepend(`/${opeCode}/`),
    R.join('')
  )(getAllAncestors([], allResources)(resource))
)

export const injectLinkTo = curryExts((
  scope: Resource,
  opeCode: string,
  allResources: Resources,
  resource: Resource
): Resource => (
  R.assoc('linkTo', {
    pathname: getResourcePathname(opeCode, allResources, scope, resource),
    query: { ...R.prop('query', resource) }
  }, resource)
))

const getFirstAncestorThatIs = curryExts((
  predicate: (res: Resource) => boolean,
  child: Resource,
  allResources: Resources
) => R.compose(
  R.when(
    RE.isNotNil,
    R.when(
      R.compose(R.not, predicate),
      getFirstAncestorThatIs(predicate)(R.__, allResources)
    )
  ),
  findParent(child)
)(allResources))

export const getFirstCatalogAncestor: GetFirstCatalogAncestor = getFirstAncestorThatIs(isTypeCatalog)

export const equalsResource = curryExts((
  res1: Resource,
  res2: Resource
) => Boolean(res1) && Boolean(res2) && R.eqBy(R.prop('key'), res1, res2))

export const getVPBonus: GetVPBonus = R.path(['content', 'hangs', 'vpBonus'])

export const findPrimaryNavigationNameInAncestors: FindPrimaryNavigationNameInAncestors = ancestors => R.pipe(
  R.ifElse(
    R.isNil,
    R.always(''),
    R.compose(
      R.find(isPrimaryNavigation),
      R.reverse
    )
  ),
  R.path(['content', 'title']),
  R.defaultTo('')
)(ancestors)

export const findSubNavigationNameInAncestors: FindSubNavigationNameInAncestors = ancestors => R.pipe(
  R.ifElse(
    R.isNil,
    R.always(''),
    R.compose(
      R.find(R.both(isNotPrimaryNavigation, isTypeNavigation)),
      R.reverse
    )
  ),
  R.path(['content', 'title']),
  R.defaultTo('')
)(ancestors)

export const getProductTitle: GetProductTitle = R.path(['content', 'title'])

export const getCityByCode: GetCityByCode = cityCode => R.ifElse(
  R.compose(
    R.not,
    () => R.equals(cityCode, 'XXX')
  ),
  R.pipe(
    R.find(R.propEq('key', cityCode)),
    R.defaultTo({}),
    R.prop('label')
  ),
  R.always(undefined)
)

const formatDate: FormatDate = (format, date) => moment(date).format(format)

export const getFormattedDepartureDate: GetFormattedDepartureDate = (format, date) => (
  (date !== '')
    ? formatDate(format, date)
    : undefined
)

export const injectOrder = curryExts((filtersProducts: FiltersProducts, resource: Resource): Resource => (
  R.assoc('order', filtersProducts[resource.key]?.order, resource)
))
